/**
 * CapacityDetailsDialog Component
 *
 * Shows detailed capacity information and recommendations for a role-day
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { RoleCapacityDayExtended, CoverageMetricType } from '../types/metrics.types';
import { ProviderCapacityDay } from '../types/capacity.types';
import { formatMinutes } from '../utils/formatters';

interface CapacityDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  roleDay: RoleCapacityDayExtended;
  providerDetails: ProviderCapacityDay[];
  metricType: CoverageMetricType;
}

export default function CapacityDetailsDialog({
  open,
  onClose,
  roleDay,
  providerDetails,
  metricType,
}: CapacityDetailsDialogProps) {
  const utilization = 100 - roleDay.surplusPercent;
  const isOverCapacity = roleDay.surplusPercent < 20; // >80% utilization
  const isTight = roleDay.surplusPercent >= 20 && roleDay.surplusPercent <= 30; // 70-80% utilization

  // Generate recommendations based on capacity state
  const getRecommendations = () => {
    const recommendations: Array<{ icon: React.ReactNode; text: string }> = [];

    if (isOverCapacity) {
      recommendations.push({
        icon: <PersonAddIcon color="error" />,
        text: `Add ${roleDay.providerCount > 0 ? 'additional' : ''} ${roleDay.role} coverage on ${new Date(roleDay.date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}`,
      });

      recommendations.push({
        icon: <SwapHorizIcon color="error" />,
        text: `Reschedule ${Math.abs(Math.floor(roleDay.gapMinutes / 60))} hours of visits to adjacent days with better capacity`,
      });

      if (roleDay.visitCapacity < 1) {
        recommendations.push({
          icon: <EventAvailableIcon color="error" />,
          text: 'Consider extending shift hours or adding part-time coverage',
        });
      }
    } else if (isTight) {
      recommendations.push({
        icon: <EventAvailableIcon color="warning" />,
        text: `Monitor this day closely - only ${formatMinutes(Math.max(0, roleDay.gapMinutes))} available for new visits`,
      });

      recommendations.push({
        icon: <SwapHorizIcon color="warning" />,
        text: 'Avoid scheduling new high-priority intakes on this day unless necessary',
      });

      if (roleDay.visitCapacity === 1) {
        recommendations.push({
          icon: <LightbulbIcon color="warning" />,
          text: 'Reserve remaining capacity for urgent/emergency visits only',
        });
      }
    }

    return recommendations;
  };

  const recommendations = getRecommendations();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Capacity Details - {roleDay.role}
        <Typography variant="body2" color="text.secondary">
          {new Date(roleDay.date).toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {/* Capacity Summary */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Capacity Summary
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Total Shift Time:
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatMinutes(roleDay.totalShiftMinutes)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Scheduled Visit Time:
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatMinutes(roleDay.totalVisitMinutes)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Available Capacity:
            </Typography>
            <Typography
              variant="body2"
              fontWeight={700}
              color={roleDay.gapMinutes < 0 ? 'error' : 'success.main'}
            >
              {formatMinutes(Math.max(0, roleDay.gapMinutes))}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Utilization:
            </Typography>
            <Typography variant="body2" fontWeight={700} color={isOverCapacity ? 'error' : isTight ? 'warning.main' : 'success.main'}>
              {Math.round(utilization)}%
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Visit Capacity:
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {roleDay.visitCapacity} visits
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Avg Continuous Time:
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatMinutes(roleDay.avgContinuousMinutes)}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Provider Details */}
        {providerDetails.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Providers ({roleDay.providerCount})
            </Typography>
            {providerDetails.map((provider) => (
              <Box
                key={provider.providerId}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  py: 0.5,
                  px: 1,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  mb: 0.5,
                }}
              >
                <Typography variant="body2">{provider.providerName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.round(provider.utilization)}% utilization
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LightbulbIcon fontSize="small" color={isOverCapacity ? 'error' : 'warning'} />
                Recommendations
              </Typography>

              <Alert severity={isOverCapacity ? 'error' : 'warning'} sx={{ mb: 2 }}>
                {isOverCapacity
                  ? 'This day is over capacity. Immediate action needed to balance workload.'
                  : 'This day has tight capacity. Monitor closely and avoid adding non-essential visits.'}
              </Alert>

              <List dense>
                {recommendations.map((rec, idx) => (
                  <ListItem key={idx} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>{rec.icon}</ListItemIcon>
                    <ListItemText
                      primary={rec.text}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </>
        )}

        {!isOverCapacity && !isTight && (
          <Alert severity="success" sx={{ mt: 2 }}>
            This day has good capacity. Safe to schedule additional visits if needed.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
